import { useState } from "react";
import { toast } from "sonner";
import { useApi, useWorkspace, errMsg, label } from "@/lib/api";
import { Section, Btn, Field, Sel, Empty, useLoad } from "@/components/app/common";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const ROLES = [["farm_manager", "Farm manager"], ["contractor", "Contractor / sharemilker"], ["staff", "Staff"], ["operations", "Operations"]];
const blank = { title: "", details: "", months: [], as_task: false, assignee_role: "farm_manager", due_day: "last" };

const Toggle = ({ on, onClick, children, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`h-8 px-2.5 rounded text-xs font-semibold border transition-colors ${on ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-300 text-slate-600 hover:bg-slate-50"}`}>{children}</button>
);

function FarmPicker({ farms, groups, sel, setSel }) {
  const setMany = (ids) => setSel([...new Set([...sel, ...ids])]);
  const arrangements = [...new Set(farms.map((f) => f.arrangement).filter(Boolean))];
  return (
    <div>
      <span className="label-sm">Farms</span>
      <div className="flex flex-wrap gap-1.5 my-2">
        <Toggle onClick={() => setMany(farms.map((f) => f.id))} testid="pick-all-farms">All farms</Toggle>
        {groups.filter((g) => farms.some((f) => f.group_id === g.id)).map((g) => <Toggle key={g.id} onClick={() => setMany(farms.filter((f) => f.group_id === g.id).map((f) => f.id))} testid={`pick-group-${g.id}`}>All in {g.name}</Toggle>)}
        {arrangements.map((a) => <Toggle key={a} onClick={() => setMany(farms.filter((f) => f.arrangement === a).map((f) => f.id))} testid={`pick-arr-${a}`}>All {label(a).toLowerCase()}</Toggle>)}
        <Toggle onClick={() => setSel([])} testid="pick-clear">Clear</Toggle>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-1.5">{farms.map((f) => (
        <label key={f.id} className="flex items-center gap-2 text-sm border border-slate-200 rounded-md px-3 py-2 cursor-pointer hover:bg-slate-50">
          <input type="checkbox" className="h-4 w-4" checked={sel.includes(f.id)} onChange={() => setSel(sel.includes(f.id) ? sel.filter((x) => x !== f.id) : [...sel, f.id])} data-testid={`pick-farm-${f.id}`} />
          <span className="flex-1">{f.name}</span><span className="text-xs text-slate-400">{label(f.arrangement)}</span>
        </label>))}</div>
    </div>
  );
}

function Overview({ tick }) {
  const api = useApi();
  const [items] = useLoad(() => api.get("/policy-items/overview"), [tick]);
  if (!items) return null;
  const byTitle = Object.values(items.reduce((acc, i) => { (acc[`${i.bulk_ref || i.title.toLowerCase()}|${i.title}|${i.months.join(",")}`] ||= { title: i.title, months: i.months, rows: [] }).rows.push(i); return acc; }, {}));
  return (
    <Section title="Policy items across farms" testid="policy-overview">
      {byTitle.length === 0 ? <Empty>No policy items yet.</Empty> : (
        <ul className="divide-y divide-slate-100">{byTitle.map((g) => (
          <li key={g.rows[0].id} className="py-2 text-sm" data-testid={`overview-${g.title}`}>
            <b>{g.title}</b> <span className="text-xs text-slate-500">· {g.months.length ? g.months.map((m) => MONTHS[m - 1]).join(", ") : "every month"} · on {g.rows.length} farm{g.rows.length > 1 ? "s" : ""}: {g.rows.map((r) => r.farm_name).join(", ")}</span>
          </li>))}</ul>)}
      <p className="text-xs text-slate-500 mt-3">Each farm has its own copy. Edit or remove a farm's copy from that farm's Calendar → Manage items.</p>
    </Section>
  );
}

export default function PolicySetup() {
  const api = useApi();
  const { allFarms, groups } = useWorkspace();
  const farms = allFarms.filter((f) => !f.archived);
  const [f, setF] = useState(blank);
  const [sel, setSel] = useState([]);
  const [res, setRes] = useState(null);
  const [tick, setTick] = useState(0);
  const toggleMonth = (m) => setF({ ...f, months: f.months.includes(m) ? f.months.filter((x) => x !== m) : [...f.months, m] });
  const save = async () => {
    try {
      const r = await api.post("/policy-items/bulk", { ...f, farm_ids: sel });
      setRes(r.results); setTick((t) => t + 1);
      const failed = r.results.filter((x) => !x.ok).map((x) => x.farm_id);
      if (r.created && !failed.length) { toast.success(`Added to ${r.created} farm${r.created > 1 ? "s" : ""}`); setF(blank); setSel([]); }
      else if (r.created) { toast.success(`Added to ${r.created}; ${failed.length} not added — still ticked so you can adjust and retry`); setSel(failed); }
      else toast.error("Nothing was added — see the notes below");
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-6">
      <Section title="Add a policy item to several farms" testid="policy-setup">
        <div className="space-y-4">
          <Field label="Item"><input className="field-input" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="e.g. Check effluent system" data-testid="bulk-policy-title" /></Field>
          <Field label="Details (optional)"><input className="field-input" value={f.details} onChange={(e) => setF({ ...f, details: e.target.value })} data-testid="bulk-policy-details" /></Field>
          <div><span className="label-sm">Months (none selected = every month)</span>
            <div className="flex flex-wrap gap-1.5 mt-1.5">{MONTHS.map((l, i) => <Toggle key={l} on={f.months.includes(i + 1)} onClick={() => toggleMonth(i + 1)} testid={`bulk-month-${i + 1}`}>{l}</Toggle>)}</div>
          </div>
          <label className="flex items-center gap-2 text-sm font-medium">
            <input type="checkbox" className="h-4 w-4" checked={f.as_task} onChange={(e) => setF({ ...f, as_task: e.target.checked })} data-testid="bulk-as-task" />Also create as a task each month
          </label>
          {f.as_task && <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Task goes to each farm's"><Sel testid="bulk-assignee-role" value={f.assignee_role} onChange={(v) => setF({ ...f, assignee_role: v })} options={ROLES.map(([value, l]) => ({ value, label: l }))} /></Field>
            <Field label="Due"><Sel testid="bulk-due-day" value={String(f.due_day)} onChange={(v) => setF({ ...f, due_day: v === "last" ? "last" : Number(v) })}
              options={[{ value: "last", label: "Last day of the month" }, ...Array.from({ length: 31 }, (_, i) => ({ value: String(i + 1), label: `Day ${i + 1} of the month` }))]} /></Field>
          </div>}
          <FarmPicker farms={farms} groups={groups} sel={sel} setSel={setSel} />
          <Btn onClick={save} disabled={!f.title.trim() || !sel.length} data-testid="bulk-policy-save">Add to {sel.length} farm{sel.length === 1 ? "" : "s"}</Btn>
          {res && <ul className="text-sm space-y-1" data-testid="bulk-results">{res.map((r) => (
            <li key={r.farm_id} className={r.ok ? "text-green-700" : "text-red-700"} data-testid={`bulk-result-${r.farm_id}`}>{r.farm_name || r.farm_id}: {r.ok ? "added" : `not added — ${r.reason}`}</li>))}</ul>}
        </div>
      </Section>
      <Overview tick={tick} />
    </div>
  );
}
